import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { authApi, ApiRequestError, type StaffUser } from "@/lib/api";
import {
  bootstrapAuthenticatedStaff,
  markAuthBootstrapSettled,
  resetAuthBootstrapState,
} from "@/lib/auth-bootstrap";
import {
  getStaffUser,
  invalidateClientSession,
  isPublicPath,
  setStoredStaff,
} from "@/lib/auth";

type SessionContextValue = {
  staff: StaffUser | null;
  hasSession: boolean;
  sessionReady: boolean;
  invalidateSession: () => void;
  refreshStaff: () => Promise<StaffUser | null>;
  establishSession: (staff: StaffUser) => void;
};

const SessionContext = createContext<SessionContextValue>({
  staff: null,
  hasSession: false,
  sessionReady: false,
  invalidateSession: () => {},
  refreshStaff: async () => null,
  establishSession: () => {},
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffUser | null>(null);
  const [hasSession, setHasSession] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  const invalidateSession = useCallback(() => {
    setStaff(null);
    setHasSession(false);
    setSessionReady(true);
    resetAuthBootstrapState();
    markAuthBootstrapSettled();
    invalidateClientSession();
  }, []);

  const refreshStaff = useCallback(async () => {
    try {
      const me = await authApi.getStaffMe();
      setStoredStaff(me);
      setStaff(me);
      setHasSession(true);
      markAuthBootstrapSettled();
      return me;
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 429) {
        const cached = getStaffUser();
        if (cached) {
          setStaff(cached);
          setHasSession(true);
          markAuthBootstrapSettled();
          return cached;
        }
      }
      invalidateSession();
      return null;
    }
  }, [invalidateSession]);

  const establishSession = useCallback((nextStaff: StaffUser) => {
    setStoredStaff(nextStaff);
    setStaff(nextStaff);
    setHasSession(true);
    markAuthBootstrapSettled();
    setSessionReady(true);
  }, []);

  useEffect(() => {
    const onInvalidate = () => {
      setStaff(null);
      setHasSession(false);
    };
    window.addEventListener("th:session-invalidated", onInvalidate);
    return () => window.removeEventListener("th:session-invalidated", onInvalidate);
  }, []);

  useEffect(() => {
    const onRestored = () => {
      void refreshStaff();
    };
    window.addEventListener("th:session-restored", onRestored);
    return () => window.removeEventListener("th:session-restored", onRestored);
  }, [refreshStaff]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const me = await bootstrapAuthenticatedStaff();
      if (cancelled) return;

      if (me) {
        setStaff(me);
        setHasSession(true);
        setSessionReady(true);
        return;
      }

      setStaff(null);
      setHasSession(false);
      invalidateClientSession();
      setSessionReady(true);

      if (
        typeof window !== "undefined" &&
        !isPublicPath(window.location.pathname)
      ) {
        const returnUrl = `${window.location.pathname}${window.location.search}`;
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`, {
          replace: true,
        });
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const value = useMemo(
    () => ({
      staff,
      hasSession,
      sessionReady,
      invalidateSession,
      refreshStaff,
      establishSession,
    }),
    [staff, hasSession, sessionReady, invalidateSession, refreshStaff, establishSession],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  return {
    ...context,
    /** @deprecated Use `staff` */
    user: context.staff,
    /** @deprecated Use `refreshStaff` */
    refreshUser: context.refreshStaff,
  };
}

/** Gate authenticated API queries until bootstrap completes. */
export function useAuthenticatedQueryEnabled(): boolean {
  const { hasSession, sessionReady } = useSession();
  return hasSession && sessionReady;
}
